import React, { useState } from "react";
import { Button } from "@mui/material";
import { ButtonVariantOption } from "@/components/Button";
import {
  DEFAULT_ROAD_COLOR,
  ROAD_CLASS_CODE_MAP,
  ROAD_CLASS_COLORS,
  ROAD_WIDTH_CODE_MAP,
  ROAD_WIDTH_COLORS,
} from "@/features/map/constants/roadLayer";
import {
  TRUSTART_CHIBAN_COVERAGE_LEGEND_COLOR,
  TRUSTART_CHIBAN_PARCEL_STROKE_COLOR,
} from "@/features/map/constants/trustartChibanLayer";
import {
  REGISTRATION_MAP_COVERAGE_LEGEND_COLOR,
  REGISTRATION_MAP_PARCEL_STROKE_LEGEND_COLOR,
} from "@/features/map/constants/registrationMapLayer";
import { useFeatureFlags } from "@/configs/featureFlag";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { PIN_COLORS } from "@/features/map/utils/canvasPin/teardropPin";
import { PinLegendIcon } from "./PinLegendIcon";

export interface LegendProps {
  title: string;
  tableData: Array<{
    color: string;
    label: string;
    hatch?: boolean;
    /** 色の四角の代わりに出す見本（ピンなど） */
    icon?: React.ReactNode;
  }>;
  /** 凡例の下に表示する補足説明（任意） */
  note?: string;
}

/*
凡例コンポーネント（単体）
*/
export const Legend: React.FC<LegendProps> = ({ title, tableData, note }) => {
  return (
    <div
      style={{
        backgroundColor: "rgb(255, 255, 255, 0.9)",
        margin: "10px 10px 25px",
        padding: "10px",
        border: "1px solid #ccc",
      }}
    >
      <div>
        {title.split("\\n").map((line, index) => (
          <div key={index}>{line}</div>
        ))}
      </div>
      <div style={{ marginTop: 2 }}>
        {tableData.map((data, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "2px",
            }}
          >
            {data.icon !== undefined ? (
              <span
                style={{
                  display: "inline-flex",
                  justifyContent: "center",
                  width: "1em",
                  marginRight: "0.5em",
                }}
              >
                {data.icon}
              </span>
            ) : (
              <i
                style={{
                  background: data.hatch
                    ? `repeating-linear-gradient(
                      -45deg,
                      transparent,
                      transparent 2px,
                      ${data.color} 2px,
                      ${data.color} 4px
                    )`
                    : data.color,
                  height: "1em",
                  width: "1em",
                  marginRight: "0.5em",
                  border: data.hatch ? `1px solid ${data.color}` : "none",
                }}
              ></i>
            )}
            <span>{data.label}</span>
          </div>
        ))}
      </div>
      {note !== undefined && (
        <div
          style={{
            marginTop: 4,
            fontSize: "0.75rem",
            color: "#555",
            maxWidth: "14em",
          }}
        >
          {note}
        </div>
      )}
    </div>
  );
};

interface LegendsProps {
  setIsShow: (isShow: boolean) => void;
  /** 登記所備付地図を表示できる（フラグ ON かつタイル公開済み）か。凡例の出し分けに使う */
  registrationMapAvailable?: boolean;
}

const roadClassTableData = Object.entries(ROAD_CLASS_CODE_MAP).map(
  ([code, label]) => ({
    color: ROAD_CLASS_COLORS[code] ?? DEFAULT_ROAD_COLOR,
    label,
  })
);

const roadWidthTableData = Object.entries(ROAD_WIDTH_CODE_MAP).map(
  ([code, label]) => ({
    color: ROAD_WIDTH_COLORS[code] ?? DEFAULT_ROAD_COLOR,
    label,
  })
);

/*
凡例コンポーネント（リスト）
*/
export const Legends: React.FC<LegendsProps> = ({
  setIsShow,
  registrationMapAvailable = false,
}) => {
  const { roadLayer: isRoadLayerEnabled, trustartChibanMap } =
    useFeatureFlags();
  // 暫定: 役所地番地図の凡例は利用可能なテナントのみ表示する
  const currentUser = useCurrentUser();
  const isMunicipalParcelMapAvailable =
    currentUser?.isMunicipalParcelMapAvailable ?? false;

  // 地図のピン（形と色は地図の描画と同じ定数を参照する）
  const pinTableData = [
    {
      color: PIN_COLORS.listed,
      label: "物件リストに入っている物件",
      icon: <PinLegendIcon kind="teardrop" color={PIN_COLORS.listed} />,
    },
    {
      color: PIN_COLORS.realEstateSearch,
      label: "不動産検索結果",
      icon: (
        <PinLegendIcon kind="teardrop" color={PIN_COLORS.realEstateSearch} />
      ),
    },
    {
      color: PIN_COLORS.savedPlot,
      label: "保存した物件リスト（リストごとに色分け）",
      icon: <PinLegendIcon kind="teardrop" color={PIN_COLORS.savedPlot} />,
    },
    {
      color: PIN_COLORS.receptionBook,
      label: "受付帳情報",
      icon: <PinLegendIcon kind="teardrop" color={PIN_COLORS.receptionBook} />,
    },
    {
      color: PIN_COLORS.requestedPicture,
      label: "解析済み登記情報",
      icon: (
        <PinLegendIcon kind="teardrop" color={PIN_COLORS.requestedPicture} />
      ),
    },
    {
      color: PIN_COLORS.listed,
      label: "情報を表示中の地点",
      icon: (
        <PinLegendIcon kind="teardrop" color={PIN_COLORS.listed} selected />
      ),
    },
    {
      color: "",
      label: "情報が見つからなかった地点",
      icon: <PinLegendIcon kind="plain" />,
    },
  ];

  // 役所地番地図（色はレイヤーの描画と同じ定数を参照する）
  const chibanMapTableData = [
    {
      color: TRUSTART_CHIBAN_PARCEL_STROKE_COLOR,
      label: "筆界（地番ポリゴン）",
    },
    { color: TRUSTART_CHIBAN_COVERAGE_LEGEND_COLOR, label: "対応エリア" },
  ];

  // 登記所備付地図（色はレイヤーの描画と同じ定数を参照する）
  const registrationMapTableData = [
    {
      color: REGISTRATION_MAP_PARCEL_STROKE_LEGEND_COLOR,
      label: "筆界（地番ポリゴン）",
    },
    { color: REGISTRATION_MAP_COVERAGE_LEGEND_COLOR, label: "対応エリア" },
  ];

  const floodDepthTableData = [
    { color: "rgb(222,128,222)", label: "20m~" },
    { color: "rgb(244,139,205)", label: "10m ~ 20m" },
    { color: "rgb(255,152,151)", label: "5m ~ 10m" },
    { color: "rgb(255,187,186)", label: "3m ~ 5m" },
    { color: "rgb(254,218,194)", label: "0.5m ~ 3m" },
    { color: "rgb(246,246,172)", label: "~0.5m" },
  ];

  const floodTimeTableData = [
    { color: "rgb(104 14 104)", label: "4週間以上" },
    { color: "rgb(182 12 108)", label: "2週間以上" },
    { color: "rgb(255 51 14)", label: "1週間~2週間" },
    { color: "rgb(252 157 11)", label: "3日~1週間" },
    { color: "rgb(250 246 14)", label: "1日~3日" },
    { color: "rgb(13 75 254)", label: "12時間~1日" },
    { color: "rgb(165 212 254)", label: "12時間未満" },
  ];

  const landslideTableData = [
    { color: "#FB684C", label: "急傾斜・特別警戒" },
    { color: "#FFED4C", label: "急傾斜・警戒" },
    { color: "#C04C63", label: "土石流・特別警戒" },
    { color: "#EDD86F", label: "土石流・警戒" },
    { color: "#CA4C95", label: "地すべり・特別警戒" },
    { color: "#FFB74C", label: "地すべり・警戒" },
  ];

  const tsunamiStormSurgeTableData = [
    { color: "#DC7ABC", label: "20m~" },
    { color: "#F285C9", label: "10~20m" },
    { color: "#FF9191", label: "5~10m" },
    { color: "#FFB7B7", label: "3~5m" },
    { color: "#FFD8C0", label: "0.5~3m" },
    { color: "#F8E1A6", label: "0.5~1m" },
    { color: "#F7F5A9", label: "~0.5m" },
    { color: "#FFFFB3", label: "~0.3m" },
  ];

  const fireProtectionAreaTableData = [
    { color: "#EA3278", label: "防火地域", hatch: true },
    { color: "#E1AD01", label: "準防火地域", hatch: true },
  ];

  const highLevelAreaTableData = [
    { color: "#E2D5BB", label: "高度地区", hatch: false },
  ];

  const highLevelUseAreaTableData = [
    { color: "#FBE5B8", label: "高度利用地区", hatch: false },
  ];

  const landslidePreventionTableData = [
    { color: "#d956844D", label: "地すべり防止地区" },
  ];

  const disasterTableData = [
    { color: "rgb(107 161 213)", label: "水害（河川）" },
    { color: "rgb(20 128 254)", label: "水害（海）" },
    { color: "rgb(13 75 254)", label: "水害（河川・海）" },
    { color: "#FFED4C", label: "急傾斜地崩壊等" },
    { color: "#FFB74C", label: "地すべり等" },
    { color: "rgb(255 51 14)", label: "火山被害" },
    { color: "rgb(212 212 212)", label: "その他" },
  ];

  return (
    <div
      style={{
        backgroundColor: "rgb(255, 255, 255, 0.9)",
        padding: "10px",
        position: "relative",
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "5px",
          right: "5px",
        }}
      >
        <Button
          variant={ButtonVariantOption.Outlined}
          onClick={() => {
            setIsShow(false);
          }}
          sx={{
            padding: "2px",
            fontSize: "0.8rem",
            borderRadius: 0,
          }}
        >
          閉じる
        </Button>
      </div>

      <div
        style={{
          marginTop: "20px",
          maxHeight: "50vh",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "2px",
          }}
        >
          <Legend
            tableData={pinTableData}
            title="地図のピン"
            note="検索結果や保存した物件リストなどのピンは、物件リストに入ると青に変わります。情報を表示している地点は、黄色の縁で示します。"
          />
          {Boolean(trustartChibanMap) && isMunicipalParcelMapAvailable && (
            <Legend
              tableData={chibanMapTableData}
              title="役所地番地図"
              note="対応エリアは地図を縮小した際に表示され、地番地図（筆界・地番）を提供している範囲を示します。拡大すると筆界と地番が表示されます。"
            />
          )}
          {registrationMapAvailable && (
            <Legend
              tableData={registrationMapTableData}
              title="登記所備付地図"
              note="対応エリアは地図を縮小した際に表示され、登記所備付地図（筆界・地番）を提供している範囲を示します。拡大すると筆界と地番が表示されます。"
            />
          )}
          <Legend
            tableData={floodDepthTableData}
            title="洪水浸水想定区域\n（計画規模）"
          />
          <Legend
            tableData={floodDepthTableData}
            title="洪水浸水想定区域\n（想定最大規模）"
          />
          <Legend
            tableData={floodTimeTableData}
            title="浸水継続時間\n（想定最大規模）"
          />
          <Legend tableData={landslideTableData} title="土砂災害警戒区域" />
          <Legend
            tableData={tsunamiStormSurgeTableData}
            title="津波浸水想定（最大浸水深クラス）"
          />
          <Legend
            tableData={tsunamiStormSurgeTableData}
            title="高潮浸水想定（最大浸水深クラス）"
          />
          <Legend
            tableData={landslidePreventionTableData}
            title="地すべり防止地区"
          />
          <Legend
            tableData={fireProtectionAreaTableData}
            title="防火・準防火地域"
          />
          <Legend tableData={disasterTableData} title="災害危険区域" />
          {isRoadLayerEnabled && (
            <>
              <Legend
                tableData={roadClassTableData}
                title="道路データ\n（道路分類種別）"
              />
              <Legend
                tableData={roadWidthTableData}
                title="道路データ\n（幅員区分）"
              />
            </>
          )}
          <Legend tableData={highLevelAreaTableData} title="高度地区" />
          <Legend tableData={highLevelUseAreaTableData} title="高度利用地区" />
        </div>
      </div>
    </div>
  );
};

/*
凡例コントロールコンポーネント
*/
interface LegendsControlProps {
  /** 登記所備付地図を表示できる（フラグ ON かつタイル公開済み）か */
  registrationMapAvailable?: boolean;
}

export const LegendsControl: React.FC<LegendsControlProps> = ({
  registrationMapAvailable = false,
}) => {
  const [isShow, setIsShow] = useState(false);
  return (
    <>
      {!isShow && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsShow(!isShow);
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onDoubleClick={(e) => {
            e.stopPropagation();
          }}
          style={{
            padding: "10px",
            backgroundColor: "#FFF",
            cursor: "pointer",
          }}
          className="leaflet-control-layers leaflet-touch"
        >
          凡例
        </button>
      )}
      {isShow && (
        <Legends
          setIsShow={setIsShow}
          registrationMapAvailable={registrationMapAvailable}
        />
      )}
    </>
  );
};
